import type {
  Conversation,
  ConversationMessage,
  Decision,
  ExecutionPlan,
  ExecutionRecord,
  Goal,
  Reflection,
} from "./types";
import type {
  ApprovalEvent,
  ApprovalRequest,
  ContextSource,
  ProductContext,
  SafeReasoningSummary,
  TimelineEvent,
  ToolIntent,
} from "./liveTypes";

export interface CyanIntelligenceRepository {
  resolveOrganizationId(id: string): Promise<string>;
  saveGoal(goal: Goal): Promise<void>;
  savePlan(plan: ExecutionPlan): Promise<void>;
  saveDecision(decision: Decision): Promise<void>;
  saveReflection(reflection: Reflection): Promise<void>;
  saveConversation(conversation: Conversation): Promise<void>;
  saveMessage(
    conversationId: string,
    message: ConversationMessage,
  ): Promise<void>;
  saveExecution(record: ExecutionRecord): Promise<void>;
  saveReasoningSummary(summary: SafeReasoningSummary): Promise<void>;
  saveApproval(approval: ApprovalRequest): Promise<void>;
  saveApprovalEvent(event: ApprovalEvent): Promise<void>;
  saveContextSelection(
    context: ProductContext & {
      conversationId?: string;
      correlationId: string;
      owner: string;
    },
  ): Promise<void>;
  saveToolIntent(intent: ToolIntent): Promise<void>;
  saveTimelineEvent(event: TimelineEvent): Promise<void>;
  saveContextSource(source: ContextSource): Promise<void>;
  deleteConversation(conversationId: string): Promise<void>;
  listGoals(filter?: { productId?: string }): Promise<Goal[]>;
  listPlans(filter?: { productId?: string }): Promise<ExecutionPlan[]>;
  listDecisions(filter?: { productId?: string }): Promise<Decision[]>;
  listReflections(filter?: { productId?: string }): Promise<Reflection[]>;
  listConversations(filter?: { productId?: string }): Promise<Conversation[]>;
  listMessages(conversationId: string): Promise<ConversationMessage[]>;
  listExecutions(filter?: { productId?: string }): Promise<ExecutionRecord[]>;
  listReasoningSummaries(filter?: {
    conversationId?: string;
    productId?: string;
  }): Promise<SafeReasoningSummary[]>;
  listApprovals(filter?: {
    productId?: string;
    conversationId?: string;
  }): Promise<ApprovalRequest[]>;
  listToolIntents(filter?: {
    productId?: string;
    planId?: string;
  }): Promise<ToolIntent[]>;
  listTimeline(filter?: {
    productId?: string;
    conversationId?: string;
  }): Promise<TimelineEvent[]>;
  listContextSources(filter?: {
    productId?: string;
    conversationId?: string;
  }): Promise<ContextSource[]>;
}

export class InMemoryCyanIntelligenceRepository implements CyanIntelligenceRepository {
  private readonly goals = new Map<string, Goal>();
  private readonly plans = new Map<string, ExecutionPlan>();
  private readonly decisions = new Map<string, Decision>();
  private readonly reflections = new Map<string, Reflection>();
  private readonly conversations = new Map<string, Conversation>();
  private readonly messages = new Map<string, ConversationMessage[]>();
  private readonly executions = new Map<string, ExecutionRecord>();
  private readonly reasoning = new Map<string, SafeReasoningSummary>();
  private readonly approvals = new Map<string, ApprovalRequest>();
  private readonly approvalEvents = new Map<string, ApprovalEvent>();
  private readonly contextSelections = new Map<
    string,
    ProductContext & {
      conversationId?: string;
      correlationId: string;
      owner: string;
    }
  >();
  private readonly toolIntents = new Map<string, ToolIntent>();
  private readonly timeline = new Map<string, TimelineEvent>();
  private readonly contextSources = new Map<string, ContextSource>();

  async resolveOrganizationId(id: string): Promise<string> {
    return id;
  }

  async saveGoal(goal: Goal): Promise<void> {
    this.goals.set(goal.id, goal);
  }
  async savePlan(plan: ExecutionPlan): Promise<void> {
    this.plans.set(plan.id, plan);
  }
  async saveDecision(decision: Decision): Promise<void> {
    this.decisions.set(decision.id, decision);
  }
  async saveReflection(reflection: Reflection): Promise<void> {
    this.reflections.set(reflection.id, reflection);
  }
  async saveConversation(conversation: Conversation): Promise<void> {
    this.conversations.set(conversation.id, conversation);
  }
  async saveMessage(
    conversationId: string,
    message: ConversationMessage,
  ): Promise<void> {
    this.messages.set(conversationId, [
      ...(this.messages.get(conversationId) ?? []),
      message,
    ]);
  }
  async saveExecution(record: ExecutionRecord): Promise<void> {
    this.executions.set(record.id, record);
  }
  async saveReasoningSummary(summary: SafeReasoningSummary): Promise<void> {
    this.reasoning.set(summary.id, summary);
  }
  async saveApproval(approval: ApprovalRequest): Promise<void> {
    this.approvals.set(approval.id, approval);
  }
  async saveApprovalEvent(event: ApprovalEvent): Promise<void> {
    this.approvalEvents.set(event.id, event);
  }
  async saveContextSelection(
    context: ProductContext & {
      conversationId?: string;
      correlationId: string;
      owner: string;
    },
  ): Promise<void> {
    this.contextSelections.set(context.correlationId, context);
  }
  async saveToolIntent(intent: ToolIntent): Promise<void> {
    this.toolIntents.set(intent.id, intent);
  }
  async saveTimelineEvent(event: TimelineEvent): Promise<void> {
    this.timeline.set(event.id, event);
  }
  async saveContextSource(source: ContextSource): Promise<void> {
    this.contextSources.set(source.sourceId, source);
  }
  async deleteConversation(conversationId: string): Promise<void> {
    this.conversations.delete(conversationId);
    this.messages.delete(conversationId);
  }
  async listGoals(filter: { productId?: string } = {}): Promise<Goal[]> {
    return [...this.goals.values()].filter(
      (item) => !filter.productId || item.productId === filter.productId,
    );
  }
  async listPlans(
    filter: { productId?: string } = {},
  ): Promise<ExecutionPlan[]> {
    return [...this.plans.values()].filter(
      (item) => !filter.productId || item.productId === filter.productId,
    );
  }
  async listDecisions(): Promise<Decision[]> {
    return [...this.decisions.values()];
  }
  async listReflections(
    filter: { productId?: string } = {},
  ): Promise<Reflection[]> {
    return [...this.reflections.values()].filter(
      (item) => !filter.productId || item.productId === filter.productId,
    );
  }
  async listConversations(
    filter: { productId?: string } = {},
  ): Promise<Conversation[]> {
    return [...this.conversations.values()].filter(
      (item) => !filter.productId || item.productId === filter.productId,
    );
  }
  async listMessages(conversationId: string): Promise<ConversationMessage[]> {
    return (
      this.messages.get(conversationId) ??
      this.conversations.get(conversationId)?.messages ??
      []
    );
  }
  async listExecutions(): Promise<ExecutionRecord[]> {
    return [...this.executions.values()];
  }
  async listReasoningSummaries(
    filter: { conversationId?: string; productId?: string } = {},
  ): Promise<SafeReasoningSummary[]> {
    return [...this.reasoning.values()].filter(
      (item) =>
        (!filter.conversationId ||
          item.conversationId === filter.conversationId) &&
        (!filter.productId || item.productId === filter.productId),
    );
  }
  async listApprovals(
    filter: { productId?: string; conversationId?: string } = {},
  ): Promise<ApprovalRequest[]> {
    return [...this.approvals.values()].filter(
      (item) =>
        (!filter.productId || item.productId === filter.productId) &&
        (!filter.conversationId ||
          item.conversationId === filter.conversationId),
    );
  }
  async listToolIntents(
    filter: { productId?: string; planId?: string } = {},
  ): Promise<ToolIntent[]> {
    return [...this.toolIntents.values()].filter(
      (item) =>
        (!filter.productId || item.productId === filter.productId) &&
        (!filter.planId || item.planId === filter.planId),
    );
  }
  async listTimeline(
    filter: { productId?: string; conversationId?: string } = {},
  ): Promise<TimelineEvent[]> {
    return [...this.timeline.values()].filter(
      (item) =>
        (!filter.productId || item.productId === filter.productId) &&
        (!filter.conversationId ||
          item.conversationId === filter.conversationId),
    );
  }
  async listContextSources(
    filter: { productId?: string } = {},
  ): Promise<ContextSource[]> {
    return [...this.contextSources.values()].filter(
      (item) => !filter.productId || item.productId === filter.productId,
    );
  }
}
